import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card} from 'antd';
import {LegalForm, UpdateAccountInput} from 'api/generated';
import {AnchorLink, PropertyList, Stack} from 'components';
import {PhoneNumberInput} from 'components/Form';
import {UrlInput} from 'components/Form/UrlInput';
import {Formik} from 'formik';
import {Form, FormItem, Input, Select, SubmitButton} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {useBusinessCategories} from 'hooks/useBusinessCategories';
import {useCountries} from 'hooks/useCountries';
import {businessCategories} from 'lib/businessCategories';
import {formatAddress, formatEmail, formatPhone, formatWebsite} from 'lib/formaters';
import {legalFormLabels} from 'lib/labels';
import {omitEmpty} from 'lib/omitEmpty';
import {pick} from 'lib/utils';
import React, {useState} from 'react';
import {SectionProps} from './types';

const {Option} = Select;

const allowedFields = [
  'country',
  'legalForm',
  'companyName',
  'mcc',
  'servicesDescription',
  'address',
  'phone',
  'email',
  'website'
];

export const BusinessDetails: React.FC<SectionProps> = ({
  cardProps,
  formProps,
  account,
  onSubmit
}) => {
  const {getIsoCountry, getCountryName, isoCountries} = useCountries();
  const {isAdmin} = useAccessLevel();
  const categories = useBusinessCategories();
  const [isEditing, setEditing] = useState(false);

  if (!account) return <Card {...cardProps} title="Business details" />;
  const business = account.business;

  const initialValues: UpdateAccountInput = {
    id: account.id,
    business: business ? omitEmpty(pick(business, allowedFields)) : {legalForm: LegalForm.COMPANY}
  };

  return (
    <Card
      {...cardProps}
      title="Business details"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik
          onSubmit={async (values) => {
            await onSubmit(values);
            setEditing(false);
          }}
          initialValues={initialValues}>
          {({values, setFieldValue}) => {
            const selectedCountry = getIsoCountry(values.business?.country);
            return (
              <Form {...formProps}>
                <FormItem name="business.country" label="Country">
                  <Select
                    name="business.country"
                    placeholder="Country"
                    optionFilterProp="children"
                    onChange={(value) => {
                      setFieldValue('business.country', value);
                      setFieldValue('business.address.country', value);
                    }}
                    showSearch>
                    {isoCountries.map((country) => (
                      <Option key={country.code} value={country.code}>
                        {country.name}
                      </Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem name="business.legalForm" label="Type of business">
                  <Select name="business.legalForm">
                    <Option value={LegalForm.COMPANY}>Company</Option>
                    <Option value={LegalForm.FREELANCE}>Individual (Autónomo)</Option>
                  </Select>
                </FormItem>
                <FormItem name="business.companyName" label="Business name">
                  <Input name="business.companyName" placeholder="My Company" />
                </FormItem>
                <FormItem name="business.mcc" label="Business category">
                  <Select
                    name="business.mcc"
                    placeholder="Business category"
                    optionFilterProp="label"
                    showSearch
                    options={categories}
                  />
                </FormItem>
                <FormItem name="business.servicesDescription" label="Business description">
                  <Input.TextArea
                    name="business.servicesDescription"
                    placeholder="We sell shoes online in the e-commerce store."
                  />
                </FormItem>
                <FormItem name="business.website" label="Business website">
                  <UrlInput name="business.website" placeholder="mycompany.com" />
                </FormItem>
                <FormItem name="business.email" label="Business email">
                  <Input name="business.email" placeholder="email@example.com" />
                </FormItem>
                <FormItem name="business.address.state" label="State / Province / Region">
                  {selectedCountry?.divisions ? (
                    <Select
                      name="business.address.state"
                      optionFilterProp="children"
                      showSearch
                      placeholder="State / Province / Region">
                      {Object.keys(selectedCountry.divisions)
                        .sort((code1, code2) =>
                          selectedCountry.divisions[code1].localeCompare(
                            selectedCountry.divisions[code2]
                          )
                        )
                        .map((code) => (
                          <Option key={code} value={selectedCountry.divisions[code]}>
                            {selectedCountry.divisions[code]}
                          </Option>
                        ))}
                    </Select>
                  ) : (
                    <Input name="business.address.state" placeholder="State / Province / Region" />
                  )}
                </FormItem>
                <FormItem name="business.address.city" label="City">
                  <Input name="business.address.city" placeholder="City" />
                </FormItem>
                <FormItem name="business.address.line1" label="Address">
                  <Input name="business.address.line1" placeholder="Street, 123" />
                </FormItem>
                <FormItem name="business.address.zip" label="Zip / Postal code">
                  <Input name="business.address.zip" placeholder="Zip / Postal code" />
                </FormItem>
                <FormItem name="business.phone" label="Business phone">
                  <PhoneNumberInput name="business.phone" countryCode={values.business?.country} />
                </FormItem>
                <FormItem
                  wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                  name="submit">
                  <Stack>
                    <Button onClick={() => setEditing(false)}>Cancel</Button>
                    <SubmitButton>Submit</SubmitButton>
                  </Stack>
                </FormItem>
              </Form>
            );
          }}
        </Formik>
      ) : (
        business && (
          <>
            <PropertyList
              omitEmpty
              emptyText="No business details provided"
              properties={[
                {label: 'Country', value: getCountryName(business.country)},
                {label: 'Type', value: legalFormLabels[business.legalForm!]},
                {label: 'Name', value: business.companyName},
                {
                  label: 'Category',
                  value: business.mcc ? businessCategories[business.mcc]?.description : null
                },
                {label: 'Description', value: business.servicesDescription},
                {label: 'Website', value: formatWebsite(business.website)},
                {label: 'Phone', value: formatPhone(business.phone, business.country, true)},
                {label: 'Address', value: formatAddress(business.address)},
                {
                  label: 'Business email',
                  value: formatEmail(business.email)
                }
              ]}
            />
          </>
        )
      )}
    </Card>
  );
};
